import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import apiUseCases from "@src/cloud-connect/api";
import { cleanupGatewayUseCase } from "@src/cloud-connect/namespace-configuration/use-cases/cleanup-gateway-use-case";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import { GatewayCleanupResponse } from "@src/types/namespace-configuration";

export interface CleanupGatewayRow {
  gatewayId: string;
  name: string;
}

export interface CleanupGatewayResultRow {
  gatewayId: string;
  gatewayName: string;
  success: boolean;
  cleanupResponse?: GatewayCleanupResponse;
  errorMessage?: string;
}

interface GatewaysWithSourcesResponse {
  connectedGatewaysListData: {
    name: string;
    gateway_uuid?: string;
  }[];
  disconnectedGatewaysListData: unknown[];
}

export const useCleanupViewAdminPanelHook = () => {
  const i18n = useI18N();
  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );
  const [gateways, setGateways] = useState<CleanupGatewayRow[]>([]);
  const [isLoadingGateways, setIsLoadingGateways] = useState(true);
  const [selectedGatewayIds, setSelectedGatewayIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [results, setResults] = useState<CleanupGatewayResultRow[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const texts = {
    title: i18n("modules.cleanupViewAdminPanel.title"),
    description: i18n("modules.cleanupViewAdminPanel.description"),
    gatewayName: i18n("modules.cleanupViewAdminPanel.gatewayName"),
    gatewayId: i18n("modules.cleanupViewAdminPanel.gatewayId"),
    noGatewaysFound: i18n("modules.cleanupViewAdminPanel.noGatewaysFound"),
    deleteButton: i18n("modules.cleanupViewAdminPanel.deleteButton"),
    resultsTitle: i18n("modules.cleanupViewAdminPanel.resultsTitle"),
    resultSuccess: i18n("modules.cleanupViewAdminPanel.resultSuccess"),
    deletedClients: i18n("modules.cleanupViewAdminPanel.deletedClients"),
    deletedDataSources: i18n(
      "modules.cleanupViewAdminPanel.deletedDataSources",
    ),
    deletedConnectionResults: i18n(
      "modules.cleanupViewAdminPanel.deletedConnectionResults",
    ),
    namespace: i18n("modules.cleanupViewAdminPanel.namespace"),
    gatewayFound: i18n("modules.cleanupViewAdminPanel.gatewayFound"),
    announcementDeleted: i18n(
      "modules.cleanupViewAdminPanel.announcementDeleted",
    ),
    yes: i18n("modules.cleanupViewAdminPanel.yes"),
    no: i18n("modules.cleanupViewAdminPanel.no"),
    serviceUserDeleted: i18n(
      "modules.cleanupViewAdminPanel.serviceUserDeleted",
    ),
    serviceUserDeletionFailed: i18n(
      "modules.cleanupViewAdminPanel.serviceUserDeletionFailed",
    ),
    serviceUserNotAttempted: i18n(
      "modules.cleanupViewAdminPanel.serviceUserNotAttempted",
    ),
    resultFailed: i18n("modules.cleanupViewAdminPanel.resultFailed"),
  };

  const failedToFetchGatewaysMessage = i18n(
    "modules.cleanupViewAdminPanel.failedToFetchGateways",
  );

  const loadGateways = useCallback(async () => {
    if (!authContext) return;

    setIsLoadingGateways(true);
    try {
      const response = (await apiUseCases.getGatewaysWithSources()) as
        | GatewaysWithSourcesResponse
        | undefined;
      if (!response) {
        errorSnackbar(failedToFetchGatewaysMessage);
        setGateways([]);
        return;
      }

      setGateways(
        // Only connected gateways are offered for cleanup; disconnected
        // gateways have nothing left to clean up on the backend.
        (response.connectedGatewaysListData ?? [])
          .filter((gateway) => gateway.gateway_uuid)
          .map((gateway) => ({
            gatewayId: gateway.gateway_uuid as string,
            name: gateway.name,
          })),
      );
    } catch (error) {
      console.error(error);
      errorSnackbar(failedToFetchGatewaysMessage);
    } finally {
      setIsLoadingGateways(false);
    }
  }, [authContext, failedToFetchGatewaysMessage]);

  useEffect(() => {
    void loadGateways();
  }, [loadGateways]);

  const toggleGateway = useCallback((gatewayId: string) => {
    setSelectedGatewayIds((current) =>
      current.includes(gatewayId)
        ? current.filter((id) => id !== gatewayId)
        : [...current, gatewayId],
    );
  }, []);

  const toggleAllGateways = useCallback(() => {
    setSelectedGatewayIds((current) =>
      current.length === gateways.length
        ? []
        : gateways.map((gateway) => gateway.gatewayId),
    );
  }, [gateways]);

  const allGatewaysSelected =
    gateways.length > 0 && selectedGatewayIds.length === gateways.length;
  const someGatewaysSelected =
    selectedGatewayIds.length > 0 && !allGatewaysSelected;

  const openConfirmDialog = useCallback(() => {
    setShowConfirmDialog(true);
  }, []);

  const closeConfirmDialog = useCallback(() => {
    if (!isDeleting) {
      setShowConfirmDialog(false);
    }
  }, [isDeleting]);

  const selectedGateways = gateways.filter((gateway) =>
    selectedGatewayIds.includes(gateway.gatewayId),
  );

  const confirmCleanup = useCallback(async () => {
    setIsDeleting(true);
    const collectedResults: CleanupGatewayResultRow[] = [];
    // cleanupGatewayUseCase never throws; it maps every failure to
    // { success: false, error }.
    for (const gateway of selectedGateways) {
      const result = await cleanupGatewayUseCase(gateway.gatewayId);
      collectedResults.push({
        gatewayId: gateway.gatewayId,
        gatewayName: gateway.name,
        success: result.success && !!result.data,
        cleanupResponse: result.data,
        errorMessage: result.error,
      });
    }
    setResults(collectedResults);
    setIsDeleting(false);
    setShowConfirmDialog(false);
    setSelectedGatewayIds([]);
    void loadGateways();
  }, [loadGateways, selectedGateways]);

  return {
    allGatewaysSelected,
    closeConfirmDialog,
    confirmCleanup,
    gateways,
    isLoadingGateways,
    isDeleting,
    openConfirmDialog,
    results,
    selectedGatewayIds,
    selectedGateways,
    showConfirmDialog,
    someGatewaysSelected,
    texts,
    toggleAllGateways,
    toggleGateway,
  };
};
